import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Route, Routes, Navigate, useLocation, Link } from 'react-router-dom';
import {
  Drawer, List, ListItem, ListItemIcon, ListItemText, AppBar, Toolbar, CssBaseline, Box, IconButton, Typography, Divider, Avatar, Menu, MenuItem, Badge, styled, CircularProgress, useMediaQuery, useTheme,
} from '@mui/material';
import {
  FaUser, FaCar, FaCalendarAlt, FaMoneyCheck, FaSignOutAlt, FaCog, FaHome, FaUsers, FaChartLine,
} from 'react-icons/fa';
import { Notifications as NotificationsIcon, Menu as MenuIcon, Close as CloseIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';

// Lazy-loaded components for admin
const DashboardHome = lazy(() => import('./DashboardHome'));
const Profile = lazy(() => import('./Profile'));
const AdminBookings = lazy(() => import('./Bookings')); // Using separate AdminBookings
const Services = lazy(() => import('./Services'));
const Payments = lazy(() => import('./Payments'));



// Authentication logic
const getUserData = () => {
  try {
    const storedData = localStorage.getItem('userData');
    return storedData ? JSON.parse(storedData) : null;
  } catch (error) {
    console.error('Failed to parse userData from localStorage:', error);
    return null;
  }
};

const isAuthenticated = () => {
  return !!getUserData();
};

const getUserType = () => {
  const userData = getUserData();
  return userData ? userData.role : null;
};

const ProtectedRoute = ({ children }) => {
  return isAuthenticated() && getUserType() === 'admin' ? children : <Navigate to="/login" replace />;
};

// Styles
const drawerWidth = 280;
const collapsedDrawerWidth = 70;

const ScrollableBox = styled(Box)({
  overflow: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});

const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
    <CircularProgress />
  </Box>
);

const AdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [userName, setUserName] = useState('Admin');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const userData = getUserData();
    if (userData && userData.name) {
      setUserName(userData.name);
    } else if (!isAuthenticated() || getUserType() !== 'admin') {
      window.location.href = '/login';
    }

    setNotifications([
      { id: 1, message: 'New booking received', time: '1 hour ago' },
      { id: 2, message: 'Payment pending', time: '3 hours ago' },
      { id: 3, message: 'User reported issue', time: '5 hours ago' },
    ]);

    if (isMobile) {
      setMobileOpen(false);
    } else {
      setDesktopOpen(true);
    }

    setIsLoading(false);
  }, [isMobile]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDesktopOpen(!desktopOpen);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('userData');
    window.location.href = '/login';
  };

  // Admin-specific menu items
  const menuItems = [
    { path: '/admin-dashboard/home', name: 'Home', icon: <FaHome /> },
    { path: '/admin-dashboard/profile', name: 'Profile', icon: <FaUser /> },
    { path: '/admin-dashboard/bookings', name: 'Bookings', icon: <FaCalendarAlt /> },
    { path: '/admin-dashboard/services', name: 'Services', icon: <FaCar /> },
    { path: '/admin-dashboard/payments', name: 'Payments', icon: <FaMoneyCheck /> },
    { path: '/login', name: 'Logout', icon: <FaSignOutAlt />, action: handleLogout },
  ];

  const drawer = (
    <Box sx={{ height: '100vh', background: 'linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)', color: 'white', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '64px !important' }}>
        {desktopOpen && (
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff', ml: 1 }}>
            ArmourPro Admin
          </Typography>
        )}
        <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
          {isMobile ? <CloseIcon /> : desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Toolbar>
      <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
      <ScrollableBox sx={{ flexGrow: 1 }}>
        <List>
          {menuItems.map((item) => (
            <ListItem
              button
              key={item.path}
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={() => {
                if (item.action) {
                  item.action();
                  if (isMobile) setMobileOpen(false);
                } else {
                  if (isMobile) setMobileOpen(false);
                }
              }}
              sx={{
                margin: '4px 8px',
                borderRadius: '8px',
                backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.3)' : 'transparent',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                justifyContent: !desktopOpen && !isMobile ? 'center' : 'flex-start',
                px: !desktopOpen && !isMobile ? 0 : 2,
                py: 1.5,
                color: 'white',
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: !desktopOpen && !isMobile ? 'auto' : '56px' }}>
                {item.icon}
              </ListItemIcon>
              {desktopOpen && (
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                    fontSize: '0.9rem',
                    color: 'white',
                  }}
                />
              )}
            </ListItem>
          ))}
        </List>
      </ScrollableBox>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: desktopOpen ? `calc(100% - ${ drawerWidth}px)` : `calc(100% - ${ collapsedDrawerWidth}px)`, xs: '100%' },
          ml: { sm: desktopOpen ? `${ drawerWidth}px` : `${ collapsedDrawerWidth}px`, xs: 0 },
          background: 'linear-gradient(90deg, #1e40af 0%, #1e3a8a 100%)',
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <FaCar style={{ fontSize: '28px', marginRight: '10px' }} />
            <Typography variant="h6" noWrap sx={{ fontWeight: 'bold', color: 'white' }}>
              ARMOURPRO ADMIN
            </Typography>
          </Box>
          <IconButton color="inherit" sx={{ mr: 1 }}>
            <Badge badgeContent={notifications.length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
              <Avatar sx={{ width: 40, height: 40, backgroundColor: theme.palette.secondary.main }}>
                {userName.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            {!isMobile && (
              <Typography variant="body1" sx={{ ml: 1, mr: 1, fontWeight: 'bold', color: 'white' }}>
                {userName}
              </Typography>
            )}
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': { width: 32, height: 32, ml: -0.5, mr: 1 },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
          >
            <MenuItem onClick={() => { window.location.href = '/admin-dashboard/profile'; handleMenuClose(); }}>
              <ListItemIcon><FaUser fontSize="small" /></ListItemIcon>
              My Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><FaSignOutAlt fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { xs: mobileOpen ? drawerWidth : 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth, borderRight: 'none' } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: desktopOpen ? drawerWidth : collapsedDrawerWidth,
              borderRight: 'none',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <ScrollableBox
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: desktopOpen ? `calc(100% - ${ drawerWidth}px)` : `calc(100% - ${ collapsedDrawerWidth}px)`, xs: '100%' },
          ml: { sm: desktopOpen ? `${ drawerWidth}px` : `${ collapsedDrawerWidth}px`, xs: 0 },
          height: '100vh',
          backgroundColor: '#f8fafc',
        }}
      >
        <Toolbar />
        <Box sx={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', p: 3, minHeight: 'calc(100vh - 64px - 24px)' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="home" element={<ProtectedRoute><DashboardHome /></ProtectedRoute>} />
                <Route path="profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="bookings" element={<ProtectedRoute><AdminBookings /></ProtectedRoute>} />
                <Route path="services" element={<ProtectedRoute><Services /></ProtectedRoute>} />
                <Route path="payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
                <Route path="" element={<Navigate to="home" replace />} />
                <Route path="*" element={<Navigate to="/admin-dashboard/home" replace />} />
              </Routes>
            </Suspense>
          )}
        </Box>
      </ScrollableBox>
    </Box>
  );
};

export default AdminDashboard;